/**
 * Runtime configuration for CDN-deployed kiosk app
 * Allows backend endpoints to be configured per-kiosk without rebuilding
 *
 * Configuration is read from URL parameters and persisted in the URL
 * across all navigation. URL parameters are REQUIRED - the app will not
 * work without them.
 */

export interface RuntimeConfig {
  apiBaseUrl: string;
  wsBaseUrl: string;
  kioskId?: string;
}

// URL parameter names
export const URL_PARAMS = {
  apiUrl: 'apiUrl',
  wsUrl: 'wsUrl',
  kioskId: 'kioskId'
} as const;

export interface ConfigParseResult {
  config: RuntimeConfig | null;
  error: string | null;
  missingParams: string[];
}

/**
 * Parse runtime config from URL parameters
 * Returns error if required params (apiUrl, wsUrl) are missing
 */
export function parseConfigFromUrl(): ConfigParseResult {
  const params = new URLSearchParams(window.location.search);
  const apiUrl = params.get(URL_PARAMS.apiUrl);
  const wsUrl = params.get(URL_PARAMS.wsUrl);
  const kioskId = params.get(URL_PARAMS.kioskId);

  const missingParams: string[] = [];
  if (!apiUrl) missingParams.push('apiUrl');
  if (!wsUrl) missingParams.push('wsUrl');

  if (missingParams.length > 0) {
    return {
      config: null,
      error: `Missing required URL parameters: ${missingParams.join(', ')}`,
      missingParams
    };
  }

  return {
    config: {
      apiBaseUrl: apiUrl!,
      wsBaseUrl: wsUrl!,
      kioskId: kioskId || undefined
    },
    error: null,
    missingParams: []
  };
}

/**
 * Get the current config URL search params string
 * Returns empty string if using defaults
 */
export function getConfigSearchParams(): string {
  const params = new URLSearchParams(window.location.search);
  const configParams = new URLSearchParams();

  const apiUrl = params.get(URL_PARAMS.apiUrl);
  const wsUrl = params.get(URL_PARAMS.wsUrl);
  const kioskId = params.get(URL_PARAMS.kioskId);

  if (apiUrl) configParams.set(URL_PARAMS.apiUrl, apiUrl);
  if (wsUrl) configParams.set(URL_PARAMS.wsUrl, wsUrl);
  if (kioskId) configParams.set(URL_PARAMS.kioskId, kioskId);

  const str = configParams.toString();
  return str ? `?${str}` : '';
}

/**
 * Validate a URL string
 */
export function isValidUrl(url: string, protocols: string[] = ['http:', 'https:', 'ws:', 'wss:']): boolean {
  try {
    const parsed = new URL(url);
    return protocols.includes(parsed.protocol);
  } catch {
    return false;
  }
}
